import { ReactNode, useState, useEffect } from 'react';
import { Grid, Box, Divider, Stack, Button, Menu, MenuItem, Card, Alert, Typography, Tooltip, CircularProgress, Collapse } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import {
	Abc as AbcIcon,
	AlternateEmail as AlternateEmailIcon,
	People as PeopleIcon,
	Tour as TourIcon,
	AccessTime as AccessTimeIcon,
	DateRange as DateRangeIcon,
	FlightLand as FlightLandIcon,
	Language as LanguageIcon,
	PhoneAndroid as PhoneAndroidIcon,
	Translate as TranslateIcon,
	Hotel as HotelIcon,
	Place as PlaceIcon,
	Info as InfoIcon,
	Check as CheckIcon,
	ShortText as ShortTextIcon,
	KeyboardDoubleArrowRight as KeyboardDoubleArrowRightIcon
} from '@mui/icons-material';
import { useLocalization } from 'hooks/useLocalization';
import { useActivityShopUI } from 'hooks/useActivityShopUI';
import { useBusiness } from 'hooks/useBusiness';
import { PaxQtyAgeGroup, ActivityInteractionData } from 'interfaces/ActivityShopUIInterfaces';
import { PhoneData, PassengerData, BookingStatus } from 'interfaces/BookingInterfaces';
import { AgeGroupKey, AdditionalClientData } from "interfaces/BusinessInterfaces";
import CenteredContent from 'components/shared/CenteredContent';
import CartItemList from 'components/activity-display/control-header/CartItemList';
import { useForm, useFieldArray, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { emailRegex } from 'utils/regex';
import { FormFields, FormField } from 'components/shared/FormFields';
import GeneralDialog from 'components/shared/GeneralDialog';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { formatDateToYmd } from 'utils/formatUtils';
import PaddingWrap from 'components/shared/PaddingWrap';
import CustomGoogleMap from 'components/shared/CustomGoogleMap';


type LodgingSource = 'custom' | 'google';

interface Lodging {
	id: string;
	name: string;
	address: string;
	coordinates: {
		lat: number;
		lng: number;
	},
	source: LodgingSource
}

interface FormData {
	contactIsOnTrip: boolean;
    contactName: string;
    contactEmail: string;
    contactPhones: PhoneData[];
	contactCountry: string;
	lodging?: Lodging;
	pickupLocationId?: number;
	passengers: PassengerData[];
	arrivalDate?: Dayjs | null;
	departureDate?: Dayjs | null;
	arrivalTime?: Dayjs | null;
	departureTime?: Dayjs | null;
	/* 
    message?: string;  // Optional if the message is not required */
}

interface BookingActivities {
	id: number;
	addedAs: 'group' | 'private';
	dateSelectedYmd: string | undefined;
	timeSelected: string | null;
	departureIdSelected: number | null;
	addedAddonIds: number[];
}

function Checkout() {
	const { getStr, langCode } = useLocalization();
	const { getTotalPaxQty, paxArr, activitiesInteractionData, selectedCurrencyCode, applyAgentDiscount, applyAgentSeasons } = useActivityShopUI();
	const { businessSlug, businessToken, countryOptions, getBizConfigVar, sendRequest, activities, setPageTitle } = useBusiness();
	const [activitiesInteractionDataCheckout, setActivitiesInteractionDataCheckout] = useState<ActivityInteractionData[]>(activitiesInteractionData);
	const [selectedCurrencyCodeCheckout, setSelectedCurrencyCodeCheckout] = useState<string>(selectedCurrencyCode);
	const [bookingIsAvailable, setBookingIsAvailable] = useState<boolean>(false);
	const [bookingStatus, setBookingStatus] = useState<BookingStatus>('N');
	const [soldOutActivityIds, setSoldOutActivityIds] = useState<number[]>([]);
	const [datesAreInvalid, setDatesAreInvalid] = useState<boolean>(false);
	const [isCreatingBooking, setIsCreatingBooking] = useState<boolean>(false);
	const [isCheckingEmail, setIsCheckingEmail] = useState<boolean>(false);
	const [checkEmailError, setCheckEmailError] = useState<string | null>(null);
	const [userExists, setUserExists] = useState<boolean>(false);
	const [user, setUser] = useState<any | null>(null);
	const [isPredictingBookingStatus, setIsPredictingBookingStatus] = useState<boolean>(true);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [openLanguageMenuIndex, setLanguageMenuIndex] = useState<number | null>(null);
	const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);
	const [errorDialogContent, setErrorDialogContent] = useState<ReactNode>(null);
	const [isBookingCreated, setIsBookingCreated] = useState<boolean>(false);
	const [arrivalTimeHi, setArrivalTimeHi] = useState<string | null>(null);
	const [departureTimeHi, setDepartureTimeHi] = useState<string | null>(null);

	const navigate = useNavigate();
	
	const languageOptions = getBizConfigVar('languageOptions');
	const bizContactEmails = getBizConfigVar('bizContactEmails');
	const languageInUse = languageOptions.find((lang) => lang.code === langCode);
	
	const useLodgings = getBizConfigVar('useLodgings');
	const usePickupLocations = getBizConfigVar('usePickupLocations');
	const pickupLocations = getBizConfigVar('pickupLocations');

	const clientDataStd = getBizConfigVar('clientDataStd');
	const clientDataCustom = getBizConfigVar('clientDataCustom');

	const hideInfo = getBizConfigVar('hideInfo');

	setPageTitle(getStr('Checkout'));
	
	const stdDataIsRequired = {
		contactCountry: clientDataStd?.country?.requireCustInput ? true : false,
		contactPhones: clientDataStd?.phone?.requireCustInput ? true : false,
		lodging: clientDataStd?.lodging?.requireCustInput ? true : false,
		paxNames: clientDataStd?.paxNames?.requireCustInput ? true : false,
		paxEmails: clientDataStd?.paxEmails?.requireCustInput ? true : false
	};
		
	const formSchema = Yup.object({
		contactIsOnTrip: Yup.boolean().required(),
		contactName: Yup.string().required(getStr('Please_fill_out_this_field')),
		contactEmail: Yup.string().required(getStr('Please_fill_out_this_field')).matches(emailRegex, getStr('Email_invalid')),
		contactPhones: (stdDataIsRequired.contactPhones && !userExists)
		? Yup.array().of(
			Yup.object().shape({
				countryCode: Yup.string().required(getStr('Please_fill_out_this_field')),
				number: Yup.string().required(getStr('Please_fill_out_this_field')),
				useWhatsApp: Yup.boolean()
			}).required(getStr('Please_fill_out_this_field'))
		).required(getStr('Please_fill_out_this_field'))
		: Yup.array().of(
			Yup.object().shape({
				countryCode: Yup.string(),
				number: Yup.string(),
				useWhatsApp: Yup.boolean()
			})
		),
		contactCountry: stdDataIsRequired.contactCountry ? Yup.string().required(getStr('Please_fill_out_this_field')) : Yup.string().notRequired(),
		//country: Yup.string().required(getStr('Please_fill_out_this_field')),
		lodging: stdDataIsRequired.lodging
		? Yup.mixed<Lodging>()
			.required(getStr('Please_fill_out_this_field'))
			.test('is-valid-lodging', getStr('Please_fill_out_this_field'), value => {
			  return typeof value === 'object' && value !== null && !!value.id; // Ensure lodging is a valid object with an ID
			})
		: Yup.mixed<Lodging>().notRequired(),
		pickupLocationId: usePickupLocations
			? Yup.number().required(getStr('Please_fill_out_this_field'))
			: Yup.number().notRequired(),
		passengers: Yup.array().of(
			Yup.object().shape({
				name: stdDataIsRequired.paxNames ? Yup.string().required(getStr('Please_fill_out_this_field')) : Yup.string().notRequired(),
				email: stdDataIsRequired.paxEmails ? Yup.string().required(getStr('Please_fill_out_this_field')) : Yup.string()
				.notRequired()
				.matches(emailRegex, {
					message: getStr('Email_invalid'),
					excludeEmptyString: true,
				}),
				type: Yup.mixed<AgeGroupKey>().required(),
				languages: Yup.array().of(Yup.string().required()).required()
		})).required(),
		arrivalDate: Yup.mixed<Dayjs>().notRequired(),
		departureDate: Yup.mixed<Dayjs>().notRequired(),
		arrivalTime: Yup.mixed<Dayjs>().notRequired(),
		departureTime: Yup.mixed<Dayjs>().notRequired(),
		
		/* 
		message: Yup.string().max(500, 'Message cannot exceed 500 characters') */
	});
	
	const bookingActivities: BookingActivities[] = activitiesInteractionDataCheckout
	.filter((activity) => activity.isAdded && (activity.addedAs === 'group' || activity.addedAs === 'private'))
	.map((addedActivity) => {
		if (addedActivity.addedAs !== 'private' && addedActivity.addedAs !== 'group') {
			throw new Error('Invalid addedAs value');
		}

		const selectedDate = addedActivity.dateSelected[addedActivity.addedAs];
		const selectedDateYmd = selectedDate ? formatDateToYmd(selectedDate) : undefined;

		return {
			id: addedActivity.activityId,
			addedAs: addedActivity.addedAs,
			dateSelectedYmd: selectedDateYmd,
			timeSelected: addedActivity.timeSelected[addedActivity.addedAs],
			departureIdSelected: addedActivity.departureIdSelected,
			addedAddonIds: addedActivity.addons[addedActivity.addedAs]
				.filter((addon) => addon.isAdded)
				.map((addon) => {
					return addon.addonId;
				})
		};
	});

	const countrySelectOptions = countryOptions.map((country) => ({ value: country.code, label: country.title }));
	const pickupLocationSelectOptions = pickupLocations.map((pickupLocation) => ({ value: pickupLocation.id, label: pickupLocation.name, description: pickupLocation.description }));

	function generatePassengerDetails(paxArr: PaxQtyAgeGroup[], clientDataCustom: AdditionalClientData[]) {
		// Sort the array to ensure 'adult' types come first
		const sortedPaxArr = paxArr.sort((a, b) => {
			if (a.type === 'adult') return -1; // Return -1 to sort 'adult' to an earlier index
			if (b.type === 'adult') return 1;  // Return 1 to sort 'adult' to an earlier index for 'b'
			return 0;                         // Return 0 to keep elements unchanged relative to each other
		});
		
		return sortedPaxArr.reduce((acc, paxType) => {
			const paxDetails: PassengerData[] = Array.from({ length: paxType.qty }, (_, i) => ({
				name: '',
				email: '',
				type: paxType.type,
				languages: [],
				customData: clientDataCustom.map(customField => {
					if (customField.onlyText) { //Is text field
						return {
							id: customField.id,
							inputText: ''
						};
					} else { //Is radio button
						if (customField.yesNoDetails) { //Requires details of answer
							return {
								id: customField.id,
								inputRadio: '',
								inputDetails: ''
							};
						} else { //Does not require details of answer
							return {
								id: customField.id,
								inputRadio: ''
							};
						}
					}
				})
			}));
			
			return acc.concat(paxDetails);
		}, [] as PassengerData[]);
	}

	const passengersData: PassengerData[] = generatePassengerDetails(paxArr, clientDataCustom);
	
	const defaultFormValues: FormData = {
		contactIsOnTrip: true,
		contactName: '',
		contactEmail: '',
		contactCountry: '',
		contactPhones: [{ countryCode: undefined, number: '', useWhatsApp: false }], // Default with one empty phone
		lodging: undefined,
		pickupLocationId: undefined,
		passengers: passengersData
	};

	const { handleSubmit, control, setValue, setError, getValues, watch, trigger, formState: { errors } } = useForm<FormData>({
        resolver: yupResolver(formSchema) as unknown as Resolver<FormData>,
		defaultValues: defaultFormValues,
		mode: 'onChange'
    });

	const { fields: phoneFields/* , append, remove */ } = useFieldArray({
        control,
        name: 'contactPhones'
    });

	const watchedPassengers = watch('passengers'); // This will subscribe to passengers changes
	const watchedLodging = watch('lodging');
	const watchedPickupLocation = watch('pickupLocationId');
	const arrivalDate = watch('arrivalDate');
	const departureDate = watch('departureDate');

	const selectedPickupLocation = pickupLocations.find((pickupLocation) => pickupLocation.id === watchedPickupLocation);

	const validateCustomData = () => {
		let hasCustomDataErrors = false;

		watchedPassengers.forEach((passenger, paxIndex) => {
			passenger.customData.forEach((paxCustomData, customDataIndex) => {
				const customData = clientDataCustom.find((customField) => customField.id === paxCustomData.id);

				if (customData?.requireCustInput) { //Requires input
					if (customData.onlyText) { //Is text
						if (!paxCustomData.inputText) {
							setError(`passengers.${paxIndex}.customData.${customDataIndex}.inputText`, {
								type: 'required'
							});

							hasCustomDataErrors = true;
						}
					} else {
						
						if (!paxCustomData.inputRadio) { //No answer is given
							setError(`passengers.${paxIndex}.customData.${customDataIndex}.inputRadio`, {
								type: 'required'
							});

							hasCustomDataErrors = true;
						
						} else if ( //Has answer
							customData.yesNoDetails && //Requires details of answer
							paxCustomData.inputRadio === 'yes' && //Answer is "yes"
							!paxCustomData.inputDetails //No details are given
						) {
							setError(`passengers.${paxIndex}.customData.${customDataIndex}.inputDetails`, {
								type: 'required'
							});

							hasCustomDataErrors = true;
						}
					}
				}
			});
		});

		return hasCustomDataErrors;
	};

	const showMissingInfoError = (infoSlugs: string[]) => {
		setShowErrorDialog(true);
		setErrorDialogContent(
			<>
				<Alert severity="error">{getStr('ERRORS_IN_FORM_PLZ_TRY_AGAIN')}</Alert>
				<ul>
					{infoSlugs.map((key) => {
						return (
							<li key={key}>{getStr('FORM_FIELD_' + key)}</li>
						)
					})}
				</ul>
			</>
		);
	};

	const onSubmit = async (data: FormData) => {
        const hasCustomDataErrors = validateCustomData();
		
		if (!hasCustomDataErrors) {
			const isFormValid = await trigger();

			if (isFormValid && !isCreatingBooking) {
				setIsCreatingBooking(true);

				const formDataFormatted = {
					...data,
					arrivalDate: data.arrivalDate?.format('YYYY-MM-DD'),
					departureDate: data.departureDate?.format('YYYY-MM-DD'),
					arrivalTime: arrivalTimeHi,
					departureTime: departureTimeHi
				}

				sendRequest({
					type: 'createBooking',
					params: {
						langCode: langCode,
						formData: formDataFormatted,
						activities: bookingActivities
					},
					onSuccess: (data) => {
						if (data.createBooking.bookingFailed) {
							switch (data.createBooking.failedReason) {
								case 'duplicateBooking':
									//Redirect to page of existing booking
									navigate('/' + getStr('PUBLIC_APP_URL_booking') + '/' + data.createBooking.duplicateBooking.bookingNumber + '/' + data.createBooking.duplicateBooking.bookingToken);
									break;

								case 'noAvailability':
									setShowErrorDialog(true);
									setErrorDialogContent(
										<Alert severity="error">{getStr('TOUR_DEP_NO_MORE_AVAIL')}</Alert>
									);
								break;

								default:
									throw new Error('Unknown booking failure reason' + data.createBooking.failedReason ? ': ' + data.createBooking.failedReason : '');
									break;
							}
						} else { //Booking created successfully
							setIsBookingCreated(true);

							setBookingStatus(data.createBooking.bookingStatus);

							if (data.createBooking.bookingStatus === 'U') { //Booking is confirmed - redirect to payments page
								navigate('/' + getStr('PUBLIC_APP_URL_booking') + '/' + data.createBooking.bookingNumber + '/' + data.createBooking.bookingToken);
							}
						}
					},
					onError: (errorMessage) => {
						console.log('error data', errorMessage);
						setShowErrorDialog(true);
						setErrorDialogContent(
							<Alert severity="error">{errorMessage}</Alert>
						);
					},
					onFinally: () => {
						setIsCreatingBooking(false);
					}
				});
			}
		} else { //Missing custom passenger info
			showMissingInfoError(['passengers']);
		}
	};

	const onError = (errors: any) => {
        // Manually validate customData fields
        const hasCustomDataErrors = validateCustomData();

		// Custom logic to handle errors, e.g., displaying a message
		const fieldOrder = [
			'contactName',
			'contactEmail',
			'contactCountry',
			'contactPhones',
			'lodging',
			'pickupLocationId',
			'arrivalDate',
			'departureDate',
			'passengers',
		];

		if (hasCustomDataErrors) { //Add passengers to error notice, if not already there
			if (!errors.passengers) {
				errors.passengers = [];
			}
		}
		
		const sortedErrorKeys = Object.keys(errors).sort((keyA, keyB) => {
			return fieldOrder.indexOf(keyA) - fieldOrder.indexOf(keyB);
		});

		showMissingInfoError(sortedErrorKeys);
	};

	useEffect(() => {
		if (bookingActivities.length > 0) {
			sendRequest({
				type: 'checkAvailability',
				params: {
					paxQty: getTotalPaxQty(paxArr),
					activities: bookingActivities
				},
				onSuccess: (data) => {
					setBookingIsAvailable(data.checkAvailability.isAvailable);
					if (data.checkAvailability.isAvailable) { //All activities are available
						setBookingStatus(data.checkAvailability.bookingStatus);
					} else { //Some activities are sold out
						setSoldOutActivityIds(data.checkAvailability.soldOutActivityIds);
					}
				},
				onError: (errorMessage) => {
					alert('error: ' + errorMessage);
					setCheckEmailError(errorMessage);
				},
				onFinally: () => {
					setIsPredictingBookingStatus(false);
				}
			});
		}
	}, []);
	
	const contactIsOnTrip = watch('contactIsOnTrip');
	
	// Generate the flat array
	const handleNameChange = (value: string) => {
		setValue('contactName', value, { shouldValidate: true });

		if (contactIsOnTrip) {
			// Set the first passenger's name
			setValue(`passengers.${0}.name`, value, { shouldValidate: true });
		}
	};

	const handleIsOnTripChange = (value: boolean) => {
		setValue('contactIsOnTrip', value);

		if (value === false) {
			// Clear the first passenger's name and email
			setValue(`passengers.${0}.name`, '', { shouldValidate: true });
			setValue(`passengers.${0}.email`, '', { shouldValidate: true });
		} else {
			// Set the first passenger's name and email
			setValue(`passengers.${0}.name`, watch('contactName'), { shouldValidate: true });
			setValue(`passengers.${0}.email`, watch('contactEmail'), { shouldValidate: true });
		}
	}

	const handleEmailChange = async (newEmail: string) => {
		if (!/^[a-zA-Z0-9._%+\-@]+$/.test(newEmail)) {
			// Handle invalid characters, e.g., show an error or just return
			return;
		}
		
		//Reset vars
		setSelectedCurrencyCodeCheckout(selectedCurrencyCode); //Reset currency
		setActivitiesInteractionDataCheckout(activitiesInteractionData); //Reset activities
		setUserExists(user !== null);

		setValue('contactEmail', newEmail, { shouldValidate: true });
		
		checkEmailError !== null && setCheckEmailError(null);
		isCheckingEmail === false && setIsCheckingEmail(true);

		if (userExists === true) {
			setUser(null);
			setValue(`passengers.${0}.name`, '', { shouldValidate: true });
			setValue(`passengers.${0}.email`, '', { shouldValidate: true });
		}

		//If contact is on trip, update email for the first passenger
		if (contactIsOnTrip) {
			setValue(`passengers.${0}.email`, newEmail, { shouldValidate: true });
		}

		if (newEmail) { //Email isn't empty
			sendRequest({
				type: 'findUserByEmail',
				params: {
					email: newEmail
				},
				onSuccess: (data) => {
					const user = data.findUserByEmail.user;
					setUserExists(user !== null);
					setUser(user);
					setCheckEmailError(null);

					if (user) { //Found user
						setValue('contactName', user.name, { shouldValidate: true });

						if (user.phones.length === 0 || !user.phones[0].number) { //Has no number
							setValue(`contactPhones.0.number`, '', { shouldValidate: true });
						} else { //Has number
							setValue(`contactPhones.0.number`, user.phones[0].number, { shouldValidate: true });
						}

						if (contactIsOnTrip) {
							// Set the first passenger's name and email
							setValue(`passengers.${0}.name`, user.name, { shouldValidate: true });
							setValue(`passengers.${0}.email`, user.email, { shouldValidate: true });
						}

						if (user.isAgent) { //User is agent
							if (user.agent.seasons.length) { //Agent has seasons
								if (user.agent.currencyCode && user.agent.currencyCode !== selectedCurrencyCodeCheckout) { //Seasons are in different currency
									setSelectedCurrencyCodeCheckout(user.agent.currencyCode);

									//Make object with JS Date types from YYYY-MM-DD
									const agentSeasonsJsDate = user.agent.seasons.map((season: any) => {
										return {
											...season,
											dates: {
												from: new Date(season.dateFromYmd),
												to: new Date(season.dateToYmd)
											}
										};
									});
									
									applyAgentSeasons(agentSeasonsJsDate, user.agent.currencyCode, activitiesInteractionDataCheckout, setActivitiesInteractionDataCheckout);
								}
							} else if (user.agent.discount) { //Has no seasons - check for discount
								applyAgentDiscount(user.agent.discount, selectedCurrencyCodeCheckout, activitiesInteractionDataCheckout, setActivitiesInteractionDataCheckout);
							}
						}
					}
				},
				onError: (errorMessage) => {
					setCheckEmailError(errorMessage);
				},
				onFinally: () => {
					setIsCheckingEmail(false);
				}
			});
		} else { //Email is empty
			setIsCheckingEmail(false);
		}
	};

	const handleArrivalDateChange = (value: Dayjs | null) => {
		setValue('arrivalDate', value, { shouldValidate: true });

		if (departureDate === null) {
			setValue('departureDate', value, { shouldValidate: true });
		}
	};

	const handleDepartureDateChange = (value: Dayjs | null) => {
		setValue('departureDate', value, { shouldValidate: true });

		if (arrivalDate === null) {
			setValue('arrivalDate', value, { shouldValidate: true });
		}
	};

	const handleArrivalTimeChange = (value: Dayjs | null) => {
		setValue('arrivalTime', value, { shouldValidate: true });

		if (value) {
			setArrivalTimeHi(value.format('HH:mm'));
		}
	};

	const handleDepartureTimeChange = (value: Dayjs | null) => {
		setValue('departureTime', value, { shouldValidate: true });

		if (value) {
			setDepartureTimeHi(value.format('HH:mm'));
		}
	};

	const checkDatesValidity = () => {
		if (!arrivalDate || !departureDate) {
			return false;  // Assume missing dates mean "not invalid"
		}
	
		const arrivalDay = arrivalDate.startOf('day');
		const departureDay = departureDate.endOf('day');
	
		for (const activity of activitiesInteractionDataCheckout) {
			if (activity.isAdded && activity.addedAs !== null) {
				const activityDate = activity.dateSelected[activity.addedAs];
				if (activityDate !== undefined) {
					const activityDateDayJs = dayjs(activityDate);
					if (activityDateDayJs.isBefore(arrivalDay, 'day') || activityDateDayJs.isAfter(departureDay, 'day')) {
						return true;  // Found an invalid date
					}
				}
			}
		}

		return false;  // No invalid dates found
	};

	const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>, paxIndex: number | null = null) => {
		setAnchorEl(event.currentTarget);
		setLanguageMenuIndex(paxIndex);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleAddLanguageClick = (languageCode: string, paxIndex: number | null = null) => {
		setAnchorEl(null); //Close language menu

		if (paxIndex === null) { //Add language to all passengers
			for (let i = 0; i < passengersData.length; i++) {
				const currentLanguages = getValues(`passengers.${i}.languages`);
		
				// Check if the languageCode is already present
				if (!currentLanguages.includes(languageCode)) {
					setValue(`passengers.${i}.languages`, [...currentLanguages, languageCode], { shouldValidate: true });
				}
			}
		} else { //Only add language to a specific passenger
			const currentLanguages = getValues(`passengers.${paxIndex}.languages`);
	
			if (!currentLanguages.includes(languageCode)) {
				setValue(`passengers.${paxIndex}.languages`, [...currentLanguages, languageCode], { shouldValidate: true });
			}
		}
	};

	const removePaxLanguage = (paxIndex: number, langCode: string) => {
		const currentLanguages = getValues(`passengers.${paxIndex}.languages`);
		const newLanguages = currentLanguages.filter((lang) => lang !== langCode);
		setValue(`passengers.${paxIndex}.languages`, newLanguages, { shouldValidate: true });
	};

	//Check if any of the activities or activity addons have unset price
	let hasUnsetPrice = false;

	activitiesInteractionDataCheckout.forEach((activityInteractionData) => {
		if (activityInteractionData.isAdded && (activityInteractionData.addedAs === 'group' || activityInteractionData.addedAs === 'private')) {
			if (!activityInteractionData.totalPrice[activityInteractionData.addedAs]) {
				hasUnsetPrice = true;
			}
			
			activityInteractionData.addons[activityInteractionData.addedAs].forEach((addon) => {
				if (addon.isAdded && !addon.totalPrice) {
					hasUnsetPrice = true;
				}
			});
		
		}
	});

	useEffect(() => {
        const isInvalid = checkDatesValidity();
        setDatesAreInvalid(isInvalid);
    }, [arrivalDate, departureDate, activitiesInteractionDataCheckout]);  // Dependencies

	return (
		<PaddingWrap>
			<CenteredContent>
				{
					bookingActivities.length > 0 ? ( //There are tours in cart
						<form
							onSubmit={handleSubmit(onSubmit, onError)}
							noValidate
						>
							<Grid container spacing={2}>
								<Grid item sm={6} xs={12}>
									<FormFields
										control={control}
										errors={errors}
										isSending={isCreatingBooking}
										disableErrorMessages={true}
									>
										<h2 style={{ marginTop: 0 }}>{getStr('Contact')}</h2>
										<FormField
											name="contactIsOnTrip"
											label={getStr('Contact_person_is_on_this_trip')}
											icon={<TourIcon />}
											type="switch"
											onChange={handleIsOnTripChange}
											defaultValue={true}
										/>
										<FormField
											name="contactName"
											label={getStr('Name')}
											icon={<AbcIcon />}
											disabled={userExists}
											onChange={handleNameChange}
											required
										/>
										<FormField
											name="contactEmail"
											label={getStr('Email')}
											icon={<AlternateEmailIcon />}
											onChange={handleEmailChange}
											isLoading={isCheckingEmail}
											required
										/>
										{
											checkEmailError && (
												<Box display="flex" alignItems="center">
													<Box sx={{ width: '35px' }} />
													<Alert severity="error" sx={{ width: '100%' }}>{checkEmailError}</Alert>
												</Box>
											)
										}
										{
											userExists && user && (
												<Box display="flex" alignItems="center">
													<Box sx={{ width: '35px' }} />
													<Alert severity={user.isAgent ? 'success' : 'info'} sx={{ width: '100%' }}>
														{getStr(user.isAgent ? 'Agent' : 'User_already_exists')}
														<div style={{ fontSize: '0.9em'}}>
														{
															user.isAgent && user.agent.businessName && (
																<div>
																	<b>{user.agent.businessName}</b>
																</div>
															)
														}
														{
															user.isAgent && !user.agent.seasons.length && user.agent.discount && (
																<div>
																	{getStr('Discount')}: {user.agent.discount}%
																</div>
															)
														}
														</div>
													</Alert>
												</Box>
											)
										}
										<FormField
											name="contactCountry"
											label={getStr('Country')}
											icon={<LanguageIcon />}
											type="select"
											options={countrySelectOptions}
											disabled={userExists}
											required={stdDataIsRequired.contactCountry}
										/>
										{phoneFields.map((field, index) => (
											<Box display="flex" alignItems="center" key={field.id}>
												<Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
													<PhoneAndroidIcon />
												</Box>
												<FormField
													name={`contactPhones.${index}.countryCode`}
													label={getStr('Country')}
													type="countryPhoneCode"
													disabled={userExists}
													required={stdDataIsRequired.contactPhones}
												/>
												<FormField
													name={`contactPhones.${index}.number`}
													label={getStr('Phone')}
													type="phoneNumber"
													disabled={userExists}
													sx={{ flexGrow: 1 }}
													required={stdDataIsRequired.contactPhones}
												/>
											</Box>
										))}
										{/* <Button variant="outlined" onClick={() => append({ countryCode: '', number: '', useWhatsApp: false })}>xAdd Phone</Button> */}
										{
											(useLodgings || usePickupLocations) && (
												<>
													<h2 style={{ marginTop: '40px' }}>{getStr('Location')}</h2>
													{
														useLodgings && (
															<>
																<FormField
																	name="lodging"
																	label={getStr('Lodging')}
																	icon={<HotelIcon />}
																	type="lodging"
																	requestType="findLodgingsByName"
																	required={stdDataIsRequired.lodging}
																/>
																{
																	(!watchedLodging && stdDataIsRequired.lodging) && (
																		<Alert severity="info">
																			{getStr('LODGING_REQUIRED_INFO_PART_1')}{' '}
																			{bizContactEmails.length ? (
																				<a href={`mailto:${bizContactEmails.join(',')}`}>{getStr('LODGING_REQUIRED_INFO_PART_contact_us')}</a>
																			) : (
																				getStr('LODGING_REQUIRED_INFO_PART_contact_us')
																			)}
																			{' '}{getStr('LODGING_REQUIRED_INFO_PART_2')}
																		</Alert>
																	)
																}
																{
																	watchedLodging && (
																		<Box
																			sx={{
																				paddingLeft: "34px"
																			}}
																		>
																			{
																				watchedLodging.address && (
																					<Typography variant="body2" color="text.secondary">{watchedLodging.address}</Typography>
																				)
																			}
																			{
																				watchedLodging.coordinates.lat && watchedLodging.coordinates.lng && (
																					<Box
																						sx={{
																							padding: '10px 0 30px 0',
																						}}
																					>
																						<Box
																							sx={{
																								boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
																							}}
																						>
																							<CustomGoogleMap
																								markerPosition={{ lat: watchedLodging.coordinates.lat, lng: watchedLodging.coordinates.lng }}
																								markerTitle={getStr('Lodging')}
																							/>
																						</Box>
																					</Box>
																				)
																			}
																		</Box>
																	)
																}
															</>
														)
													}
													{
														usePickupLocations && (
															<FormField
																name="pickupLocationId"
																label={getStr('Pickup_location')}
																icon={<PlaceIcon />}
																type="select"
																options={pickupLocationSelectOptions}
																required={true}
															/>
														)
													}
													{
														selectedPickupLocation && selectedPickupLocation.coordinates.lat && selectedPickupLocation.coordinates.lng && (
															<Box
																sx={{
																	paddingLeft: "34px"
																}}
															>
																<Box
																	sx={{
																		padding: '10px 0 30px 0',
																	}}
																>
																	<CustomGoogleMap
																		markerPosition={{ lat: selectedPickupLocation.coordinates.lat, lng: selectedPickupLocation.coordinates.lng }}
																		markerTitle={getStr('Pickup_location')}
																	/>
																</Box>
															</Box>
														)
													}
												</>
											)
										}
										{
											!hideInfo.arrivalDeparture && (
												<>
													<h2 style={{ marginTop: '40px' }}>{getStr('Arrival_and_departure')}</h2>
													<Box display="flex" alignItems="center">
														<DateRangeIcon sx={{ mr: 1, display: 'flex', alignItems: 'center' }} />
														<Grid container>
															<Grid item xs={6} sx={{ paddingRight: '6px' }}>
																<FormField
																	name="arrivalDate"
																	label={getStr('Arrival_date')}
																	type="date"
																	onChange={handleArrivalDateChange}
																	maxDate={departureDate}
																/>
															</Grid>
															<Grid item xs={6} sx={{ paddingLeft: '6px' }}>
																<FormField
																	name="departureDate"
																	label={getStr('Departure_date')}
																	type="date"
																	onChange={handleDepartureDateChange}
																	minDate={arrivalDate}
																/>
															</Grid>
														</Grid>
													</Box>
													<Box display="flex" alignItems="center">
														<AccessTimeIcon sx={{ mr: 1, display: 'flex', alignItems: 'center' }} />
														<Grid container>
															<Grid item xs={6} sx={{ paddingRight: '6px' }}>
																<FormField
																	name="arrivalTime"
																	label={getStr('Arrival_time')}
																	type="time"
																	onChange={handleArrivalTimeChange}
																/>
															</Grid>
															<Grid item xs={6} sx={{ paddingLeft: '6px' }}>
																<FormField
																	name="departureTime"
																	label={getStr('Departure_time')}
																	type="time"
																	onChange={handleDepartureTimeChange}
																/>
															</Grid>
														</Grid>
													</Box>
													<Box display="flex" alignItems="center">
														<FlightLandIcon sx={{ mr: 1, display: 'flex', alignItems: 'center' }} />
														<Grid container>
															<Grid item xs={6} sx={{ paddingRight: '6px' }}>
																<FormField
																	name="arrivalTransNo"
																	label={getStr('Arrival_flight_trans_no')}
																/>
															</Grid>
															<Grid item xs={6} sx={{ paddingLeft: '6px' }}>
																<FormField
																	name="departureTransNo"
																	label={getStr('Departure_flight_trans_no')}
																/>
															</Grid>
														</Grid>
													</Box>
												</>
											)
										}
										<h2 style={{ marginTop: '40px' }}>{getStr('Passengers')}</h2>
										<Box display="flex" alignItems="center">
											<FormField
												label={getStr('Passenger_amount')}
												defaultValue={getTotalPaxQty(paxArr)}
												icon={<PeopleIcon />}
												sx={{ flexGrow: 1}}
											/>
											{
												watchedPassengers.length > 1 && (
													<Box>
														<Button
															variant="outlined"
															startIcon={<TranslateIcon />}
															onClick={(e) => handleMenuClick(e)}
														>
															{getStr('Add_language_to_all')}
														</Button>
														<Menu
															anchorEl={anchorEl}
															open={Boolean(anchorEl) && openLanguageMenuIndex === null}
															onClose={handleClose}
															anchorOrigin={{
															vertical: 'top',
															horizontal: 'right',
															}}
															transformOrigin={{
															vertical: 'top',
															horizontal: 'right',
															}}
														>
															{
																languageInUse && [
																	<MenuItem key="languageInUse" onClick={() => {handleAddLanguageClick(languageInUse.code)}}>
																		<img
																			alt={languageInUse.title}
																			src={`https://www.tourtask.com/images/icons/flags/languages/flag-${languageInUse.code}-30x20.png`}
																			style={{ width: 30, height: 20, marginRight: 12 }}
																		/>
																		{languageInUse.title}
																	</MenuItem>,
																	<Divider key="divider" />
																]
															}
															{languageOptions.map((languageOption) => (
															<MenuItem key={languageOption.code} onClick={() => {handleAddLanguageClick(languageOption.code)}}>
																<img
																	alt={languageOption.title}
																	src={`https://www.tourtask.com/images/icons/flags/languages/flag-${languageOption.code}-30x20.png`}
																	style={{ width: 30, height: 20, marginRight: 12 }}
																/>
																{languageOption.title}
															</MenuItem>
															))}
														</Menu>
													</Box>
												)
											}
										</Box>
											{watchedPassengers.map((passenger, paxIndex) => {
												const availableLanguages = languageOptions.filter(langOption => !passenger.languages.includes(langOption.code));
		
												return (
													<div key={paxIndex}>
														<Box display="flex" alignItems="center" sx={{ p: 1, mt: 1, mb: 2, backgroundColor: 'lightgray' }}>
															<Box sx={{ flexGrow: 1, ml: 1 }}>
																<Typography sx={{ fontWeight: 'bold' }}>{`${paxIndex + 1}) ${getStr('AGE_GROUP_TITLE_' + passenger.type)}`}</Typography>
															</Box>
															<Box>
																<Stack direction="row" spacing={1} alignItems="center" sx={{ justifyContent: 'flex-end' }}>
																	{passenger.languages.map((langCode, langIndex) => {
																		const langTitle = languageOptions.find((lang) => lang.code === langCode)?.title;
		
																		if (!langTitle) return null;
		
																		return (
																			<Tooltip title={getStr('Remove_LANGUAGE', langTitle)} key={langCode}>
																				<img
																					src={`https://www.tourtask.com/images/icons/flags/languages/flag-${langCode}-30x20.png`}
																					alt={langTitle}
																					className="language-flag-removable"
																					onClick={() => {removePaxLanguage(paxIndex, langCode)}}
																				/>
																			</Tooltip>
																		)
																	})}
																	{availableLanguages.length > 0 && (
																		<>
																			<Button
																				variant="outlined"
																				size="small"
																				startIcon={<TranslateIcon />}
																				onClick={(e) => handleMenuClick(e, paxIndex)}
																				sx={{ marginBottom: passenger.languages.length > 0 ? 1 : 0 }}
																			>
																				{getStr('Add_language')}
																			</Button>
																			<Menu
																				anchorEl={anchorEl}
																				open={Boolean(anchorEl) && openLanguageMenuIndex === paxIndex}
																				onClose={handleClose}
																				anchorOrigin={{
																					vertical: 'top',
																					horizontal: 'right',
																				}}
																				transformOrigin={{
																					vertical: 'top',
																					horizontal: 'right',
																				}}
																			>
																				{
																					languageInUse && availableLanguages.includes(languageInUse) && [
																							<MenuItem onClick={() => {handleAddLanguageClick(languageInUse.code, paxIndex)}} key="languageInUse">
																								<img
																									alt={languageInUse.title}
																									src={`https://www.tourtask.com/images/icons/flags/languages/flag-${languageInUse.code}-30x20.png`}
																									style={{ width: 30, height: 20, marginRight: 12 }}
																								/>
																								{languageInUse.title}
																							</MenuItem>,
																							<Divider key="divider" />
																					]
																				}
																				{availableLanguages.map((languageOption) => (
																					<MenuItem key={languageOption.code} onClick={() => {handleAddLanguageClick(languageOption.code, paxIndex)}}>
																						<img
																							alt={languageOption.title}
																							src={`https://www.tourtask.com/images/icons/flags/languages/flag-${languageOption.code}-30x20.png`}
																							style={{ width: 30, height: 20, marginRight: 12 }}
																						/>
																						{languageOption.title}
																					</MenuItem>
																				))}
																			</Menu>
																		</>
																	)}
																</Stack>
															</Box>
														</Box>
														<Stack spacing={2}>
															<FormField
																name={`passengers.${paxIndex}.name`}
																label={getStr('Name')}
																icon={<AbcIcon />}
																showLockIcon={paxIndex === 0 && contactIsOnTrip}
																tooltip={(paxIndex === 0 && contactIsOnTrip) ? getStr('FIRST_PAX_FIELD_LOCKED_HELP') : ''}
																required={stdDataIsRequired.paxNames}
																disabled={paxIndex === 0 && contactIsOnTrip}
															/>
															<FormField
																name={`passengers.${paxIndex}.email`}
																label={getStr('Email')}
																icon={<AlternateEmailIcon />}
																showLockIcon={paxIndex === 0 && contactIsOnTrip}
																tooltip={(paxIndex === 0 && contactIsOnTrip) ? getStr('FIRST_PAX_FIELD_LOCKED_HELP') : ''}
																required={stdDataIsRequired.paxEmails}
																disabled={paxIndex === 0 && contactIsOnTrip}
															/>
															{
																passenger.customData.map((passengerCustomDataRow, customFieldIndex) => {
																	//Fetch custom data field
																	const customField: AdditionalClientData = clientDataCustom[customFieldIndex];
		
																	//Check if any of the customField.tourIds is included in any of the tours in cart
																	let activityMatchFound = false;
		
																	if (!customField.allTours) {
																		customField.tourIds.forEach((tourId) => {
																			const activity = activitiesInteractionDataCheckout.find((activity) => activity.activityId === tourId);
																			if (activity && activity.isAdded) {
																				activityMatchFound = true;
																			}
																		});
																	}
		
																	return (
																		(customField.allTours || activityMatchFound) && (
																			customField.onlyText ? ( //Only needs a text field
																				<FormField
																					key={`${customFieldIndex}`}
																					name={`passengers.${paxIndex}.customData.${customFieldIndex}.inputText`}
																					label={customField.title ? customField.title : getStr('Name_unset').toUpperCase()}
																					icon={<InfoIcon />}
																					maxLength={100}
																					required={customField.requireCustInput}
																				/>
																			) : ( //Is yes/no question
																				<div key={`${customFieldIndex}`}>
																					<FormField
																						name={`passengers.${paxIndex}.customData.${customFieldIndex}.inputRadio`}
																						label={customField.title ? customField.title : getStr('Name_unset').toUpperCase()}
																						icon={<CheckIcon />}
																						type="radio"
																						options={[
																							{ value: 'no', label: getStr('No') },
																							{ value: 'yes', label: getStr('Yes') }
																						]}
																						required={customField.requireCustInput}
																					/>
																					{
																						customField.yesNoDetails && passengerCustomDataRow.inputRadio === 'yes' && (
																							<FormField
																								name={`passengers.${paxIndex}.customData.${customFieldIndex}.inputDetails`}
																								label={`${getStr('Specify_details')}${customField.title ? (' (' + customField.title.toLowerCase() + ')') : ''}`}
																								icon={<ShortTextIcon />}
																								sx={{ marginTop: '5px'}}
																								maxLength={100}
																								required={customField.requireCustInput}
																							/>
																						)
																					}
																				</div>
																			)
																		)
																	)
																})
															}
														</Stack>
													</div>
												)
											})}
									</FormFields>
								</Grid>
								<Grid item sm={6} xs={12}>
									<Card sx={{ mb: 4 }}>
										<CartItemList
											isInteractive={false}
											activitiesInteractionData={activitiesInteractionDataCheckout}
											selectedCurrencyCode={selectedCurrencyCodeCheckout}
										/>
									</Card>
									{
										isPredictingBookingStatus ? (
											<Box display="flex" justifyContent="center">
												<CircularProgress />
											</Box>
										) : (
											bookingIsAvailable ? (
												<Stack spacing={2}>
													{datesAreInvalid && (
														<Alert severity="error">{getStr('TOURS_OUTSIDE_DATE_RANGE_CONTACT_US_TO_HELP_INFO')}</Alert>
													)}
													{hasUnsetPrice && (
														<Alert severity="error">{getStr('PRICES_UNSET_CONTACT_US_TO_HELP_INFO')}</Alert>
													)}
													<Collapse in={!isBookingCreated}>
														<LoadingButton
															loading={isCreatingBooking}
															type="submit"
															variant="contained"
															color="primary"
															size="large"
															disabled={isCheckingEmail || datesAreInvalid || hasUnsetPrice}
															endIcon={bookingStatus === 'U' ? <KeyboardDoubleArrowRightIcon /> : null}
															fullWidth
														>
															{isCreatingBooking ? (
																<CircularProgress color="inherit" size={24} />
															) : (
																<>
																	{getStr(bookingStatus === 'N' ? 'Send_inquiry' : 'Book_and_pay')}
																</>
															)}
														</LoadingButton>
													</Collapse>
													<Collapse in={isBookingCreated && bookingStatus === 'U'}>
														<Alert severity="info">
															<Typography variant="h2">{getStr('Your_booking_has_been_successfully_created_EXCL')}</Typography>
														</Alert>
													</Collapse>
													<Collapse in={isBookingCreated && bookingStatus === 'N'}>
														<Alert severity="info">
															<Typography variant="h2" sx={{mb: '5px' }}>{getStr('Inquiry_has_been_sent_successfully_EXCL')}</Typography>
															{getStr('SEND_INQUIRY_CHK_AVAIL_RETURN_ASAP')}
														</Alert>
													</Collapse>
												</Stack>
											) : ( //Booking unavailable (someone booked group tours in different session)
												<Alert severity="error">
													{getStr('SORRY_FOLLOWING_ACTIVITIES_JUST_SOLD_OUT')}
													<ul>
														{soldOutActivityIds.map((soldOutActivityId) => {
															const activity = activities.find((activity) => activity.id === soldOutActivityId);
															return (
																activity && (
																	<li key={soldOutActivityId}>
																		{activity.title}
																	</li>
																)
															)
														})
														}
													</ul>
													{getStr('Please_go_back_and_try_again')}
												</Alert>
											)
										)
									}
								</Grid>
							</Grid>
						</form>
					) : ( //No tours in cart
						<Alert severity="error">{getStr('Cart_is_empty')}</Alert>
					)
				}
				<GeneralDialog
					isOpen={showErrorDialog}
					onClose={() => setShowErrorDialog(false)}
					title={getStr('Error')}
					content={errorDialogContent}
					type="error"
				/>
			</CenteredContent>
		</PaddingWrap>
	);
}

export default Checkout;